import type { TileViewer } from "@virdx/wsi-viewer"

/** The subset of TileViewer the conversion needs (structural, so tests can stub it). */
export interface ViewerScaleSource {
  getImageInfo(): { levels: Array<{ width: number; height: number }> } | null
  getZoom(): number
  getCenter(): { x: number; y: number }
}

export interface ScreenPoint {
  x: number
  y: number
  /** True when the point lies within `margin` px of the canvas. */
  visible: boolean
}

/**
 * Level-0 image pixel → canvas CSS pixel, using the same math the viewer
 * renders with: fit-to-canvas scale × zoom around the viewport center. The
 * canvas has no devicePixelRatio scaling (`TileViewer.resize` sets canvas
 * pixels equal to CSS pixels), so the result positions absolutely-placed DOM
 * overlays such as presence cursors and the voice-note ghost cursor.
 */
export function imageToScreen(
  viewer: ViewerScaleSource | TileViewer,
  canvas: { width: number; height: number },
  imageX: number,
  imageY: number,
  margin = 20
): ScreenPoint | null {
  const info = viewer.getImageInfo()
  const level0 = info?.levels[0]
  if (!level0 || level0.width <= 0 || level0.height <= 0) return null
  const fitScale = Math.min(canvas.width / level0.width, canvas.height / level0.height)
  const scale = fitScale * viewer.getZoom()
  const center = viewer.getCenter()
  const x = (imageX - center.x) * scale + canvas.width / 2
  const y = (imageY - center.y) * scale + canvas.height / 2
  const visible = x >= -margin && y >= -margin && x <= canvas.width + margin && y <= canvas.height + margin
  return { x, y, visible }
}
